/**
 * The bridge from card buckets to their summaries.
 *
 * Subscribes to the sync store and, whenever a deck's card bucket changes
 * identity, recomputes that deck's `DeckStudySummary` from the bucket and
 * persists it — so Home reads per-deck rows and never walks the cards.
 *
 * Load-bearing rules (each one is a bug when broken):
 *
 * - COALESCED ON A MACROTASK, never a microtask. A streamed `cards-bulk-updated`
 *   burst is one store tick per frame; a microtask would recompute after every
 *   frame and double the per-frame cost this layer exists to remove. The
 *   default scheduler is `setTimeout(0)`; specs inject a synchronous one.
 * - NOT source-gated. Server frames change the counts too, so the bridge runs
 *   for `withServerSource` ticks as well (unlike the dirty scan).
 * - ONLY COMPLETE BUCKETS are summarised (`deckBucketComplete`). A partial
 *   bucket — a cold deck receiving frames, a deck still streaming in — marks
 *   the deck STALE instead: its existing row is kept (still the best answer
 *   for the tile) with `staleAtISO` set, and it is re-checked on every later
 *   tick until the bucket is complete. Persisted, so a kill in between does
 *   not hide the lag.
 * - A STALE DECK WHOSE BODIES ARE NOT RESIDENT IS RECOMPUTED FROM DISK
 *   (`disk-recompute.ts`), one deck at a time after the flush, once per bucket
 *   change. Without it a deck edited on another device while its cards are on
 *   disk here would show stale counts until it was opened. The rows it finds
 *   are PUBLISHED IN BATCHES (`createSummaryPublisher`), never one tick per
 *   deck.
 * - THE STORE WRITE RUNS UNDER `withServerSource`: `deckSummaries` is not in
 *   the sync snapshot, so nothing would be pushed either way, but a "local"
 *   tick makes the dirty scan walk the whole snapshot for nothing.
 * - ONLY A BUCKET THAT DISAPPEARS drops its summary from memory and disk (the
 *   deck or all of its cards were removed; tracked from the tick that removed
 *   it). A deck that simply has no bucket — its bodies are on disk — keeps its
 *   row; a re-created id must not inherit a removed one.
 * - BOOT TRUSTS DISK: at the hydrate tick a persisted, unmarked row of the
 *   current `DECK_STUDY_SUMMARY_VERSION` whose `rowCount` equals the bucket's
 *   size is kept as is; anything else resident is recomputed, and stale rows
 *   of non-resident decks go to the disk pass. Counts are persisted one
 *   macrotask after the card write, so the window a kill can open is that
 *   macrotask.
 */
import { DECK_STUDY_SUMMARY_TABLE } from '../../sync-engine/framework/local-tables';
import type { DeckId } from '../../sync-engine/schema/shared-types';
import { bucketRowCount, deckBucketComplete } from '../internal/deck-residency';
import { withServerSource } from '../internal/framework/mutation-source';
import { perfLog, perfNow } from '../internal/perf-log';
import type { FlashkaState } from '../internal/store/flashka-store';
import { changedProgressFloors } from '../internal/store/progress-floor';
import { reportSyncStorageError, safeBulkPut } from '../internal/store/store-utils';
import {
  getSyncStorage,
  type StoredDeckSummary,
  type SyncStorage,
} from '../provider/sync-storage';
import {
  createSummaryPublisher,
  recomputeSummaryFromDisk,
  type SummaryPublisher,
} from './disk-recompute';
import {
  DECK_STUDY_SUMMARY_VERSION,
  resetDeckStudySummary,
  summarizeDeckCards,
} from './summary';

/** The slice of the store the bridge reads and writes. Structural, so specs
 *  can pass the real store or a double. */
export type DeckSummaryStore = {
  getState: () => FlashkaState;
  subscribe: (listener: (state: FlashkaState, prev: FlashkaState) => void) => () => void;
};

/** Schedules one flush; returns a cancel. Injectable so specs run it inline. */
export type DeckSummaryScheduler = (run: () => void) => () => void;

const macrotask: DeckSummaryScheduler = (run) => {
  const timer = setTimeout(run, 0);
  return () => clearTimeout(timer);
};

let scheduler: DeckSummaryScheduler = macrotask;

/** Test seam — `null` restores the macrotask default. */
export function setDeckSummaryScheduler(next: DeckSummaryScheduler | null): void {
  scheduler = next ?? macrotask;
}

export type DeckSummaryBridge = {
  /** Unsubscribe and drop any scheduled flush. */
  stop: () => void;
  /** Run a pending flush now (specs; app-background). */
  flush: () => void;
  /** Deck ids whose summary lags their rows (bucket incomplete when it changed). */
  staleDeckIds: () => ReadonlySet<string>;
  /** Resolves once the disk recompute pass in flight, if any, has finished. */
  diskIdle: () => Promise<void>;
};

const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/**
 * The bridge currently running, so the platform layer can drain it without
 * threading a handle through React.
 *
 * It has to be drainable from outside: the flush is a macrotask, and the one
 * moment it MUST have run is the moment the runtime is about to stop giving
 * out macrotasks — app background on native, a closing tab on web. A store
 * write staged there (the bulk coalescer drains into it for exactly this
 * reason) would otherwise schedule a flush that never runs, leaving the
 * persisted summary one edit behind with nothing marking it stale, so the
 * next launch trusts it and Home shows pre-edit counts until the deck is
 * opened.
 */
let activeBridge: DeckSummaryBridge | null = null;

/** Run any pending summary recompute NOW. Safe when no bridge is running. */
export function drainDeckSummaries(): void {
  activeBridge?.flush();
}

export function startDeckSummaryBridge(
  store: DeckSummaryStore,
  storage: () => SyncStorage = getSyncStorage,
): DeckSummaryBridge {
  /** Buckets whose identity changed since the last flush. */
  const dirty = new Set<string>();
  /** Buckets removed since the last flush (present before, absent now). */
  const gone = new Set<string>();
  /** Decks whose row lags their bucket; re-checked on every flush. */
  const stale = new Set<string>();
  /** Decks whose progress-reset floor changed since the last flush. */
  const floorChanged = new Set<string>();
  /** Stale decks already probed on disk since their last bucket change. */
  const diskProbed = new Set<string>();
  /** What the last `'partial'` probe of a stale deck saw, so the next bucket
   *  change can be judged against it instead of re-reading the deck. */
  const partialProbe = new Map<string, { rowsSeen: number; bucketRows: number }>();
  let cancel: (() => void) | null = null;
  let diskPass: Promise<void> | null = null;
  let stopped = false;

  const flush = () => {
    cancel = null;
    const state = store.getState();
    if (!state.isHydrated) {
      dirty.clear();
      gone.clear();
      return;
    }
    const t0 = perfNow();
    const cards = state.cards;
    const upsert: Record<string, StoredDeckSummary> = {};
    const removed: string[] = [];
    let recomputed = 0;

    for (const id of gone) {
      if (hasOwn(cards, id)) {
        // The bucket came back before the flush ran — a normal change.
        dirty.add(id);
        continue;
      }
      stale.delete(id);
      diskProbed.delete(id);
      partialProbe.delete(id);
      if (hasOwn(state.deckSummaries, id)) removed.push(id);
    }
    gone.clear();

    // A progress reset: when every study the row counts predates the floor,
    // the reset row is exact without a card (a cold deck shows it at once);
    // otherwise the deck's rows decide — the bucket below, or the disk pass.
    for (const id of floorChanged) {
      const row = hasOwn(state.deckSummaries, id) ? state.deckSummaries[id] : undefined;
      const floor = state.deckProperties[id]?.progressResetAtISO;
      const floorMs = floor ? Date.parse(floor) : NaN;
      if (!row || row.lastStudiedAtMs === 0) continue;
      if (row.lastStudiedAtMs < floorMs) {
        upsert[id] = resetDeckStudySummary(row);
      } else if (!hasOwn(cards, id)) {
        stale.add(id);
        diskProbed.delete(id);
        partialProbe.delete(id);
      }
    }
    floorChanged.clear();

    for (const id of stale) dirty.add(id);
    for (const id of dirty) {
      const bucket = hasOwn(cards, id) ? cards[id] : undefined;
      // No bucket and not removed: the deck's bodies live on disk. Its row
      // stays; a stale one is the disk pass's job.
      if (!bucket) continue;
      const existing =
        upsert[id] ??
        (hasOwn(state.deckSummaries, id) ? state.deckSummaries[id] : undefined);
      if (!deckBucketComplete(state, id)) {
        stale.add(id);
        if (existing && !existing.staleAtISO) {
          upsert[id] = { ...existing, staleAtISO: new Date().toISOString() };
        }
        continue;
      }
      stale.delete(id);
      diskProbed.delete(id);
      partialProbe.delete(id);
      let summary: ReturnType<typeof summarizeDeckCards>;
      try {
        summary = summarizeDeckCards(bucket);
      } catch (err) {
        // This runs in a timer (and on app-state change), outside any React
        // boundary: an unguarded throw is a fatal crash, and the deck stays
        // dirty, so it would throw again on every launch. Skip the deck —
        // its row keeps its last value — and report it.
        reportSyncStorageError(err, 'deck-summary-summarize');
        continue;
      }
      upsert[id] = {
        ...summary,
        deckId: id as DeckId,
        version: DECK_STUDY_SUMMARY_VERSION,
        staleAtISO: null,
      };
      recomputed += 1;
    }
    dirty.clear();

    const rows = Object.values(upsert);
    if (rows.length > 0 || removed.length > 0) {
      withServerSource(() => state.applyDeckSummaries(upsert, removed));
      const st = storage();
      if (rows.length > 0) safeBulkPut(st, DECK_STUDY_SUMMARY_TABLE, rows);
      for (const id of removed) {
        try {
          st.removeByKey(DECK_STUDY_SUMMARY_TABLE, id);
        } catch (err) {
          reportSyncStorageError(err, 'deck-summary-remove');
        }
      }
      perfLog(
        'deck-summary flush',
        t0,
        () => ({ recomputed, stale: stale.size, removed: removed.length }),
        1,
      );
    }
    scheduleDiskPass();
  };

  const schedule = () => {
    if (cancel) return;
    cancel = scheduler(flush);
  };

  // ── Stale decks whose bodies are on disk ──────────────────────────────

  /**
   * Could re-reading this deck's rows answer differently than last time?
   *
   * A deck cold at this device's first sync can never reach its `cardCount`
   * from local rows, so every probe comes back `'partial'` — and clearing the
   * probe mark on each bucket change re-armed a full `getByIndex` (on web,
   * behind a whole storage flush) once per coalesced frame batch, for the
   * length of a stream. That is exactly the per-frame cost this layer exists
   * to remove, and the user feels it as a stalling UI while a big deck lands.
   *
   * The last probe saw `rowsSeen` rows with `bucketRows` of them in memory, so
   * the rows that arrived since can lift the total to at most
   * `rowsSeen + (bucket now − bucketRows)`. Below `cardCount` the answer is
   * still `'partial'`; at or above it the gap can close and the deck is worth
   * a read (the 98-on-disk deck whose last two rows arrive as frames).
   */
  const diskGapCanClose = (state: FlashkaState, id: string): boolean => {
    const last = partialProbe.get(id);
    if (!last) return true;
    const cardCount = state.deckProperties[id]?.cardCount;
    // Still no count: the probe cannot certify the set either way, so asking
    // again buys the same `'partial'` for a full read. A meta that later
    // GAINS a count drops the mark below and is probed again.
    if (cardCount == null) return false;
    const grown = bucketRowCount(state.cards[id]) - last.bucketRows;
    return last.rowsSeen + Math.max(grown, 0) >= cardCount;
  };

  /** The next stale deck worth a disk read: not yet probed since its last
   *  bucket change, a deck this client knows (has meta for), and one whose
   *  local set could actually have caught up. */
  const nextDiskCandidate = (state: FlashkaState): string | undefined => {
    for (const id of stale) {
      if (diskProbed.has(id)) continue;
      if (!hasOwn(state.deckProperties, id)) continue;
      if (!diskGapCanClose(state, id)) continue;
      return id;
    }
    return undefined;
  };

  const runDiskPass = async () => {
    // One store tick per batch, not per deck — a boot with many stale rows
    // walks them all, and every tick re-renders Home. Published on every
    // normal exit; a stop drops it (the bridge no longer owns the store).
    const publish = createSummaryPublisher({ store, storage: storage() });
    try {
      await probeStaleDecks(publish);
    } finally {
      if (stopped) publish.discard();
      else publish.flush();
    }
  };

  const probeStaleDecks = async (publish: SummaryPublisher) => {
    for (;;) {
      if (stopped) return;
      publish.flushIfDue();
      const state = store.getState();
      if (!state.isHydrated) return;
      const id = nextDiskCandidate(state);
      if (id === undefined) return;
      // Marked before the read: a bucket change during it clears the mark and
      // the deck is probed again with the newer rows.
      diskProbed.add(id);
      let outcome: Awaited<ReturnType<typeof recomputeSummaryFromDisk>>;
      try {
        outcome = await recomputeSummaryFromDisk(id, {
          store,
          storage: storage(),
          publish,
        });
      } catch (err) {
        reportSyncStorageError(err, 'deck-summary-disk-recompute');
        continue;
      }
      if (stopped) return;
      if (outcome.result === 'partial') {
        partialProbe.set(id, {
          rowsSeen: outcome.rowsSeen,
          bucketRows: outcome.bucketRows,
        });
      } else {
        partialProbe.delete(id);
      }
      if (outcome.result === 'written') {
        stale.delete(id);
      } else if (outcome.result === 'resident') {
        // The bucket completed meanwhile — the bucket path owns it now.
        dirty.add(id);
        schedule();
      }
      // 'partial' / 'skipped': the existing row stays, re-probed once the rows
      // that landed since could close the gap (`diskGapCanClose`).
    }
  };

  const scheduleDiskPass = () => {
    if (diskPass || stopped) return;
    if (nextDiskCandidate(store.getState()) === undefined) return;
    diskPass = runDiskPass().finally(() => {
      diskPass = null;
    });
  };

  /** The hydrate tick: every resident bucket is checked against its persisted
   *  row; a trusted row costs nothing, the rest are recomputed. Stale rows of
   *  non-resident decks go to the disk pass. */
  const boot = (state: FlashkaState) => {
    dirty.clear();
    gone.clear();
    stale.clear();
    diskProbed.clear();
    partialProbe.clear();
    const cards = state.cards;
    for (const id of Object.keys(cards)) {
      const row = hasOwn(state.deckSummaries, id) ? state.deckSummaries[id] : undefined;
      if (
        row &&
        !row.staleAtISO &&
        row.version === DECK_STUDY_SUMMARY_VERSION &&
        row.rowCount === bucketRowCount(cards[id])
      ) {
        continue;
      }
      dirty.add(id);
    }
    for (const id of Object.keys(state.deckSummaries)) {
      if (state.deckSummaries[id]?.staleAtISO) stale.add(id);
    }
    if (dirty.size > 0 || stale.size > 0) schedule();
  };

  const unsub = store.subscribe((state, prev) => {
    if (!state.isHydrated) {
      if (prev.isHydrated) {
        // reset(): the buckets are gone because the user is, not because the
        // decks were deleted — nothing to drop from disk.
        dirty.clear();
        gone.clear();
        stale.clear();
        floorChanged.clear();
        diskProbed.clear();
        partialProbe.clear();
        if (cancel) cancel();
        cancel = null;
      }
      return;
    }
    if (!prev.isHydrated) {
      boot(state);
      return;
    }
    const cards = state.cards;
    const prevCards = prev.cards;
    if (cards !== prevCards) {
      for (const id of Object.keys(cards)) {
        if (cards[id] !== prevCards[id]) {
          dirty.add(id);
          diskProbed.delete(id);
        }
      }
      for (const id of Object.keys(prevCards)) {
        if (!hasOwn(cards, id)) gone.add(id);
      }
    }
    // A stale deck's own meta changing (a count arriving, or catching up with
    // the rows this device holds) is the other thing that can change what a
    // disk probe would answer — drop its probe marks so the next pass re-reads
    // it. Per-deck, so an unrelated deck's meta write costs nothing.
    for (const id of changedProgressFloors(prev.deckProperties, state.deckProperties)) {
      floorChanged.add(id);
    }
    if (state.deckProperties !== prev.deckProperties && stale.size > 0) {
      for (const id of stale) {
        if (state.deckProperties[id] === prev.deckProperties[id]) continue;
        diskProbed.delete(id);
        partialProbe.delete(id);
      }
    }
    if (dirty.size > 0 || gone.size > 0 || floorChanged.size > 0) {
      schedule();
      return;
    }
    // A stale deck completes when its loaded flag flips or its meta catches
    // up, with no bucket change — re-check on those ticks.
    if (
      stale.size > 0 &&
      (state.loadedDeckIds !== prev.loadedDeckIds ||
        state.archivedDeckIds !== prev.archivedDeckIds ||
        state.deckProperties !== prev.deckProperties)
    ) {
      schedule();
    }
  });

  // Started after hydrate (mobile mounts the sync core once the store is
  // ready): run the boot pass for the state that is already there.
  if (store.getState().isHydrated) boot(store.getState());

  const bridge: DeckSummaryBridge = {
    stop: () => {
      stopped = true;
      if (activeBridge === bridge) activeBridge = null;
      unsub();
      if (cancel) cancel();
      cancel = null;
      dirty.clear();
      gone.clear();
      stale.clear();
      floorChanged.clear();
      diskProbed.clear();
      partialProbe.clear();
    },
    flush: () => {
      if (cancel) cancel();
      flush();
    },
    staleDeckIds: () => stale,
    diskIdle: () => diskPass ?? Promise.resolve(),
  };
  activeBridge = bridge;
  return bridge;
}
